import React from "react"
import { ProviderFinderParam, usePushUpdatedSearchParams } from "../../utils/providerFinderSearchUrl"
import { useUserContext } from "../../context/UserContext"
import {
  getAvailableParentIdsByBenefitConfig,
  PARENT_ID_MAPPING,
  PARENT_ID_TO_PROVIDER_COUNT_MAPPING,
  ProviderType
} from "../../utils/parentIdMapping"
import { Benefit } from "#/lib/carrot-api/types/Benefit"
import { FormattedMessage, useIntl } from "react-intl"
import { useIntlSort } from "../../../../utils/hooks/useIntlSort"
import { useGetHasProviderTypeInCountry } from "#/components/views/app/navigation/hooks/useGetHasProviderTypeInCountry"
import { Box, FormControl, Typography, MenuItem, Select, SelectChangeEvent } from "@carrotfertility/carotene-core"
import {
  PROVIDER_TYPE_SEARCHED,
  useProviderTypeSearched
} from "#/pages/provider-finder/components/results/useProviderTypeSearched"

export function ProviderTypeFilter({ benefit }: { benefit: Benefit }): JSX.Element {
  const { companyCountryCode } = useUserContext()
  const intl = useIntl()
  const { localeComparator } = useIntlSort()
  const { data: hasProviderTypes, isLoading: isLoadingHasProviderTypes } =
    useGetHasProviderTypeInCountry(companyCountryCode)
  const availableParentIds: string[] = getAvailableParentIdsByBenefitConfig(benefit)
  const companySupportsMultipleProviderTypes = availableParentIds.length > 1
  const { providerTypeId } = useProviderTypeSearched()
  const defaultProviderTypeIfNoneExists = availableParentIds[0]
  const currentProviderType: string = providerTypeId ?? defaultProviderTypeIfNoneExists
  const pushUpdatedSearchParams = usePushUpdatedSearchParams()

  const options = availableParentIds
    .filter((parentId) => {
      const providerType = PARENT_ID_TO_PROVIDER_COUNT_MAPPING[parentId]
      return hasProviderTypes && hasProviderTypes[providerType]
    })
    .sort((parentId1, parentId2) => {
      const parentFullDisplayName1 = intl.formatMessage(PARENT_ID_MAPPING[parentId1].displayName)
      const parentFullDisplayName2 = intl.formatMessage(PARENT_ID_MAPPING[parentId2].displayName)
      return localeComparator(parentFullDisplayName1, parentFullDisplayName2)
    })
    .map((parentId) => {
      const { displayName } = PARENT_ID_MAPPING[parentId]
      return (
        <MenuItem key={parentId} value={parentId}>
          {intl.formatMessage(displayName)}
        </MenuItem>
      )
    })

  const handleChange = (event: SelectChangeEvent<string>): void => {
    const value = event.target.value
    const newProviderType = value
    pushUpdatedSearchParams((searchParamsToUpdate: URLSearchParams) => {
      searchParamsToUpdate.set(ProviderFinderParam.ProviderType, newProviderType ?? "NONE")

      if (PROVIDER_TYPE_SEARCHED[newProviderType] !== ProviderType.Doulas) {
        searchParamsToUpdate.delete(ProviderFinderParam.DoulaServices)
      } else {
        searchParamsToUpdate.delete(ProviderFinderParam.CarrotPartner)
        searchParamsToUpdate.delete(ProviderFinderParam.ServiceFormat)
      }
      if (PROVIDER_TYPE_SEARCHED[newProviderType] !== ProviderType.FertilityClinics) {
        searchParamsToUpdate.delete(ProviderFinderParam.CarrotCenterOfExcellence)
      }
    })
  }

  if (isLoadingHasProviderTypes || !companySupportsMultipleProviderTypes) {
    return null
  }
  return (
    <FormControl fullWidth>
      <Box display="flex" flexDirection="column" gap={(theme) => theme.spacing(theme.tokens.spacing.xxs)}>
        <Typography variant="h6" paddingLeft={(theme) => theme.spacing(theme.tokens.spacing.md)}>
          <FormattedMessage defaultMessage="Provider type" />
        </Typography>
        <Select
          labelId="provider-type-filter-select-label"
          id="provider-type-filter-select"
          value={currentProviderType}
          onChange={handleChange}
          label={intl.formatMessage({ defaultMessage: "Select provider type" })}
          sx={{
            "& .MuiSelect-select": {
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "transparent"
            }
          }}
        >
          {options}
        </Select>
      </Box>
    </FormControl>
  )
}
